import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Form from "../../../components/Form/Form";
import FormTitle from "../../../components/FormTitle/FormTitle";
import Input from "../../../components/Input/Input";
import Loader from "../../../components/Loader/Loader";
import Select from "../../../components/Select/Select";
import ToggleButton from "../../../components/ToggleButton/ToggleButton";
import { useListManager } from "../../../contexts/list.manager";
import { useUserStore } from "../../../contexts/userStore";
import {
	AutomationType,
	AutomationTypeEnum,
	AutomationTypeText,
} from "../../../enums/automationTypes";
import AutomationButtonService from "../../../services/AutomationButtonService";
import "./FormAutomationButtons.css";
import { generatePastelHexColor } from "../../../utils/color";
import { IconSelector } from "../../../components/IconSelector/IconSelector";
import { MdLightbulbOutline } from "react-icons/md";

function FormAutomationButtons(props) {
	const [automationType, setAutomationType] = useState(() => {
		if (props.automationButton?.type === 1) return "pgm";
		if (props.automationButton?.type === 2) return "pgm_ip";
		return "var";
	});
	const [showLoader, setShowLoader] = useState(false);
	const [commandDescription, setCommandDescription] = useState(
		props.automationButton?.automation_description || ""
	);
	const [color, setColor] = useState(
		props.automationButton?.color || ""
	);
	const [cooldownTime, setCooldownTime] = useState(
		props.automationButton?.cooldownTime || 0
	);
	const [icon, setIcon] = useState(
		props.automationButton?.icon || ""
	);
	const [variableName, setVariableName] = useState(
		props.automationButton?.automation_variable_name || ""
	);
	const [variableNumber, setVariableNumber] = useState(
		props.automationButton?.automation_variable_number || ""
	);
	const [enabledInMonitoring, setEnabledInMonitoring] = useState(
		props.automationButton?.visible_on_monitoring || false
	);
	const [position, setPosition] = useState(
		props.automationButton?.pgm_position || ""
	);
	const [address, setAddress] = useState(props.automationButton?.address || "");
	const [serial, setSerial] = useState(props.automationButton?.serial || "");

	const { selectedCondominioObj } = useUserStore();
	const { closeForm } = useListManager();
	const navigate = useNavigate();

	useEffect(() => {
		setCommandDescription(props.automationButton?.automation_description || "");
		setVariableName(props.automationButton?.automation_variable_name || "");
		setVariableNumber(props.automationButton?.automation_variable_number || "");
		setEnabledInMonitoring(
			props.automationButton?.visible_on_monitoring || false
		);
		setPosition(props.automationButton?.pgm_position || "");
		setAddress(props.automationButton?.address || "");
		setSerial(props.automationButton?.serial || "");
		setColor(props.automationButton?.color || generatePastelHexColor());
		setCooldownTime(props.automationButton?.cooldownTime || 0);
		setIcon(props.automationButton?.icon || "");
		if (props.automationButton?.type === 1) setAutomationType("pgm");
		else if (props.automationButton?.type === 2) setAutomationType("pgm_ip");
		else setAutomationType("var");
	}, [props.automationButton]);

	// Monta o payload conforme o DTO
	const buildPayload = () => {
		const payload = {
			condominium: selectedCondominioObj,
			type: AutomationTypeEnum[automationType],
			visible_on_monitoring: enabledInMonitoring,
			automation_description: commandDescription,
		};

		if (address) payload.address = Number(address);
		if (serial) payload.serial = serial;

		if (automationType === AutomationType.VARIAVEL) {
			if (variableName) payload.automation_variable_name = variableName;
			if (variableNumber)
				payload.automation_variable_number = Number(variableNumber);
		}

		if (automationType === AutomationType.PGM) {
			if (position) payload.pgm_position = Number(position);
		}

		if (automationType === AutomationType.PGM_IP) {
			if (position) payload.pgm_position = Number(position);
		}

		payload.automation_button_on = false;
		payload.automation_button_off = false;
		payload.color = color;
		payload.icon = icon;
		payload.cooldownTime = cooldownTime;

		return payload;
	};

	async function handleSubmit(event) {
		event.preventDefault();
		if (props.formMode === "edicao") {
			handleEdit();
			return;
		}
		setShowLoader(true);
		const payload = buildPayload();
		const response = await AutomationButtonService.createAutomationButton(
			payload
		);
		setShowLoader(false);
		if (response.data.id) navigate("../consulta-automation-buttons", {
			state: { refresh: true }
		});
	}

	async function handleEdit() {
		setShowLoader(true);
		const payload = buildPayload();
		const response = await AutomationButtonService.updateAutomationButton(
			props.automationButton.id,
			payload
		);
		setShowLoader(false);
		if (response.data.id) navigate("../consulta-automation-buttons", {
			state: { refresh: true }
		});
	}

	async function handleDelete() {
		if (!props.automationButton) return;
		setShowLoader(true);
		await AutomationButtonService.deleteAutomationButton(
			props.automationButton.id
		);
		setShowLoader(false);
		closeForm();
		navigate("../consulta-automation-buttons", {
			state: { deletedId: props.automationButton.id },
		});
	}

	const handleClickEditButton = () => {
		if (!props.automationButton) return;
		navigate(`/automation-buttons/${props.automationButton.uuid}`, { state: props.automationButton });
	};

	return (
		<React.Fragment>
			<FormTitle
				type={props.formMode}
				icon={<MdLightbulbOutline size={20} />}
				tituloConsulta={props.automationButton?.automation_description || null}
				tituloCadastro={"Cadastro de Comando de Automação"}
				backRoute={"/consulta-automation-buttons"}
			/>
			{showLoader ? (
				<Loader />
			) : (
				<Form
					type={props.formMode}
					navigateCancelUrl="../consulta-automation-buttons"
					handleSubmit={handleSubmit}
					handleClickEdit={handleClickEditButton}
					handleDelete={handleDelete}
				>
					<div className="automation-select-container">
						<div className="select-automation-type">
							<Select
								labelText="Tipo de Automação"
								name="automation_type"
								id="automation_type"
								value={automationType}
								disabled={props.formMode === "consulta"}
								onChange={({ target }) => setAutomationType(target.value)}
								options={[
									...Object.entries(AutomationTypeText).map(([key, text]) => ({
										value: key,
										text,
									})),
								]}
							/>
						</div>
					</div>
					<div className="automation-buttons-grid">
						{/* Campos comuns a todos os tipos */}
						<div className="automation-buttons-group">
							<Input
								name="automation_description"
								id="automation_description"
								labelText="Descrição do comando *"
								value={commandDescription}
								onChange={({ target }) => setCommandDescription(target.value)}
								disabled={props.formMode === "consulta"}
								required={true}
							/>
						</div>
						{automationType === AutomationType.PGM && (
							<div className="automation-buttons-group">
								<Input
									name="address"
									id="address"
									labelText="Endereço*"
									type="number"
									value={address}
									onChange={({ target }) => setAddress(target.value)}
									disabled={props.formMode === "consulta"}
									required={true}
									hidden={automationType !== AutomationType.PGM}
								/>
							</div>
						)}
						{automationType === AutomationType.PGM_IP && (
							<div className="automation-buttons-group">
								<Input
									name="serial"
									id="serial"
									labelText="Serial*"
									value={serial}
									onChange={({ target }) => setSerial(target.value)}
									disabled={props.formMode === "consulta"}
									required={true}
									hidden={automationType !== AutomationType.PGM_IP}
								/>
							</div>
						)}
						{/* Campos específicos por tipo */}
						{automationType === AutomationType.VARIAVEL && (
							<>
								<div className="automation-buttons-group">
									<Input
										name="automation_variable_name"
										id="automation_variable_name"
										labelText="Nome da variável *"
										value={variableName}
										onChange={({ target }) => setVariableName(target.value)}
										disabled={props.formMode === "consulta"}
										required={true}
									/>
								</div>
								<div className="automation-buttons-group">
									<Input
										name="automation_variable_number"
										id="automation_variable_number"
										labelText="Valor da variável *"
										type="number"
										value={variableNumber}
										onChange={({ target }) => setVariableNumber(target.value)}
										disabled={props.formMode === "consulta"}
										required={true}
									/>
								</div>
							</>
						)}

						{automationType === AutomationType.PGM && (
							<div className="automation-buttons-group">
								<Input
									name="pgm_position"
									id="pgm_position"
									labelText="Posição do relay do PGM *"
									type="number"
									value={position}
									onChange={({ target }) => setPosition(target.value)}
									disabled={props.formMode === "consulta"}
									required={true}
								/>
							</div>
						)}

						{/* PGM IP pode ter campos próprios, adicione conforme necessário */}
						{automationType === AutomationType.PGM_IP && (
							<div className="automation-buttons-group">
								<Input
									name="pgm_position"
									id="pgm_position"
									labelText="Posição do relay do PGM IP *"
									type="number"
									value={position}
									onChange={({ target }) => setPosition(target.value)}
									disabled={props.formMode === "consulta"}
									required={true}
								/>
							</div>
						)}

						<div className="automation-buttons-group">
							<Input
								name="color"
								id="color"
								type="color"
								labelText="Cor"
								value={color}
								onChange={({ target }) => setColor(target.value)}
								disabled={props.formMode === "consulta"}
								required={false}
							/>
						</div>

						<div className="automation-buttons-group">
							<Input
								name="cooldownTime"
								id="cooldownTime"
								type="cooldownTime"
								labelText="Tempo de espera (ms)"
								value={cooldownTime}
								onChange={({ target }) => setCooldownTime(target.value)}
								disabled={props.formMode === "consulta"}
								required={false}
							/>
						</div>

						<div className="automation-buttons-group">
							<IconSelector
								name="icon"
								id="icon"
								type="icon"
								labelText="Ícone"
								value={icon}
								onChange={({ target }) => setIcon(target.value)}
								disabled={props.formMode === "consulta"}
								required={false}
							/>
						</div>

						{/* Botão de visibilidade no monitoramento */}

						<div className="automation-buttons-group">
							<ToggleButton
								label="Visível no monitoramento"
								selected={enabledInMonitoring}
								onClick={() => setEnabledInMonitoring(!enabledInMonitoring)}
								disabled={props.formMode === "consulta"}
							/>
						</div>
					</div>
				</Form>
			)}
		</React.Fragment>
	);
}

export default FormAutomationButtons;
